import React from 'react';
import { StyleSheet, View } from 'react-native';
import {
  BoundScrollView,
  DisplayText,
  HStack,
  JumpTarget,
  theme,
  useResponsive,
} from '@sweetgreen/garnish';

import { useLocalizationContext } from '@order/Localization';

import { useLargeDesktopBreakpoint } from '../../../hooks';
import { useProductDetailsScreenContext } from '../../../ProductDetailsScreen.provider';
import { checkIfBreadKind } from '../../../state';
import type { FilteredIngredientModificationsKinds } from '../../../types';
import { IngredientModificationCard } from '../../IngredientModificationCard';
import { ProductModificationsContainer } from './ProductModificationsContainer';

// ────────────────────────────────────────────────────────────────────────────────

export const ProductModificationsIngredientsGrid = (
  props: ProductModificationsIngredientsGridProps,
) => {
  const { ingredientsModificationsKinds } = props;
  const {
    product,
    addIngredientModification,
    removeIngredientModification,
    activeIngredientsModificationsMap,
  } = useProductDetailsScreenContext();

  // ────────────────────────────────────────────────────────────────────

  const { t } = useLocalizationContext();
  const { match } = useResponsive();
  const isLargeDesktop = useLargeDesktopBreakpoint();
  const itemsPerRow = isLargeDesktop ? 4 : match([3, 4, 3, 5]);

  // ────────────────────────────────────────────────────────────────────

  if (!product) return null;

  return (
    <BoundScrollView style={styles.wrapper}>
      <ProductModificationsContainer>
        {ingredientsModificationsKinds.map((kind) => {
          if (checkIfBreadKind(kind)) return null;
          const label = t(`pdp.modifications.${kind}`);

          const ingredientModificationKind =
            product?.ingredientModifications[kind] ?? [];

          return (
            <JumpTarget key={kind} id={kind}>
              <View style={styles.section}>
                <DisplayText size={4} style={styles.sectionTitle}>
                  {label}
                </DisplayText>

                <HStack
                  itemsPerRow={itemsPerRow}
                  customSpacing={theme.spacing.medium}
                >
                  {ingredientModificationKind.modifications.map(
                    (ingredientModification) => {
                      const { ingredient, outOfStock } = ingredientModification;
                      const isSelected = activeIngredientsModificationsMap.has(
                        ingredient.id,
                      );

                      return (
                        <IngredientModificationCard
                          key={ingredient.id}
                          ingredientModification={ingredientModification}
                          isUnavailable={outOfStock}
                          isSelected={isSelected}
                          onPress={addIngredientModification}
                          onDecreaseQuantity={removeIngredientModification}
                        />
                      );
                    },
                  )}
                </HStack>
              </View>
            </JumpTarget>
          );
        })}
      </ProductModificationsContainer>
    </BoundScrollView>
  );
};

// ─── STYLES ─────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  wrapper: {
    width: '100%',
  },
  section: {
    paddingVertical: theme.spacing.xlarge,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.LIGHT,
  },
  sectionTitle: {
    paddingBottom: theme.spacing.medium,
  },
});

// ─── TYPES ──────────────────────────────────────────────────────────────────────

type ProductModificationsIngredientsGridProps = {
  readonly ingredientsModificationsKinds: FilteredIngredientModificationsKinds;
};
